import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

// @mui/material components
import {Badge, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Tooltip} from "@mui/material";

// @mui/icons-material
import {Autorenew, NotificationsActive} from "@mui/icons-material";

// core components
import CustomButton from "components/CustomButton";
import Snackbar from "components/snackbar/snackbar";

// actions
import invoiceCatchUp from "api/retailer/invoiceCatchUp";
import invoiceGenerationStatus from "api/retailer/invoiceGenerationStatus";

InvoiceCatchUpButton.propTypes = {
    retailerId: PropTypes.string,
};

/**
 * Admin-only shortcut to the `wishibam:invoice:catch-up-retailer` command: it regenerates the
 * invoices of the current year that were never produced, typically the ones missed before invoicing
 * was switched on for this retailer.
 *
 * The badge, the tooltip and the confirmation report how many invoice documents of this retailer are
 * still travelling through the generation pipeline, catch-up or normal shipping flow alike.
 */
function InvoiceCatchUpButton({retailerId}) {
    const currentYear = new Date().getFullYear();

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isRunning, setRunning] = useState(false);
    const [notification, setNotification] = useState(null);
    // null while unknown: the hint stays out of the way rather than claiming "nothing running".
    const [pending, setPending] = useState(null);

    const refreshPending = useCallback(() => {
        if (!retailerId) {
            return;
        }

        invoiceGenerationStatus(retailerId)
            .then(status => setPending(status.pending))
            .catch(() => setPending(null));
    }, [retailerId]);

    useEffect(refreshPending, [refreshPending]);

    if (!retailerId) {
        // Creation form: there is no retailer to catch up yet.
        return null;
    }

    const hasPending = null !== pending && pending > 0;

    const confirm = () => {
        setDialogOpen(false);
        setRunning(true);

        invoiceCatchUp(retailerId, currentYear).then(result => {
            setPending(result.pending);

            if (result.blocked) {
                setNotification({
                    color: "warning",
                    messageId: `retailer.form.invoiceCatchUp.blocked.${result.blockedReason}`,
                    values: {},
                });

                return;
            }

            setNotification({
                color: "success",
                messageId: "retailer.form.invoiceCatchUp.done",
                values: {
                    year: result.year,
                    generated: result.generated,
                    skipped: result.skipped,
                    failed: result.failed,
                },
            });
        }).catch(() => {
            refreshPending();
            setNotification({
                color: "danger",
                messageId: "retailer.form.invoiceCatchUp.failed",
                values: {year: currentYear},
            });
        }).finally(() => setRunning(false));
    };

    const tooltip = (
        <>
            <FormattedMessage id={"retailer.form.invoiceCatchUp.tooltip"} values={{year: currentYear}}/>
            {hasPending && (
                <>
                    <br/>
                    <FormattedMessage id={"retailer.form.invoiceCatchUp.inProgress"} values={{count: pending}}/>
                </>
            )}
        </>
    );

    return (
        <>
            <Tooltip title={tooltip}>
                {/* A disabled IconButton fires no events, so the tooltip needs a wrapper that still does. */}
                <span>
                    <IconButton
                        aria-label={"invoice-catch-up"}
                        size={"small"}
                        color={"primary"}
                        disabled={isRunning}
                        onClick={() => {
                            refreshPending();
                            setDialogOpen(true);
                        }}
                    >
                        <Badge color={"warning"} variant={"dot"} invisible={!hasPending}>
                            {isRunning ? <CircularProgress size={20}/> : <Autorenew fontSize={"small"}/>}
                        </Badge>
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)} maxWidth={"xs"}>
                <DialogContent sx={{padding: "24px"}}>
                    <FormattedMessage id={"retailer.form.invoiceCatchUp.confirm"} values={{year: currentYear}}/>
                    {hasPending && (
                        <p style={{marginBottom: 0, color: "#8a6d3b"}}>
                            <FormattedMessage id={"retailer.form.invoiceCatchUp.inProgress"} values={{count: pending}}/>
                        </p>
                    )}
                </DialogContent>
                <DialogActions sx={{padding: "8px 24px 16px", gap: "8px"}}>
                    <CustomButton variant={"nude"} color={"blue"} onClick={() => setDialogOpen(false)}>
                        <FormattedMessage id={"list.button.cancel"}/>
                    </CustomButton>
                    <CustomButton variant={"contained"} color={"blue"} onClick={confirm}>
                        <FormattedMessage id={"retailer.form.invoiceCatchUp.confirmButton"}/>
                    </CustomButton>
                </DialogActions>
            </Dialog>

            {null !== notification && (
                <Snackbar
                    open
                    close
                    closeNotification={() => setNotification(null)}
                    // A catch-up report is worth reading: only the close button or the delay hides it.
                    onClose={(event, reason) => "clickaway" !== reason && setNotification(null)}
                    autoHideDuration={8000}
                    place={"bl"}
                    color={notification.color}
                    icon={function () {
                        return <NotificationsActive/>
                    }}
                    message={<FormattedMessage id={notification.messageId} values={notification.values}/>}
                />
            )}
        </>
    );
}

export default InvoiceCatchUpButton;
